<template>
  <div class="video-editor">
    <template v-if="activeFile">
      
      <!-- Editor Overlay Header -->
      <div class="editor-header-overlay">
        <div class="editor-info">
          {{ $t('lbl_EMPBE_Uploader_EditingVideo', { aspectRatioStr: aspectRatioStr }) }}
        </div>
        <button 
          type="button"
          v-if="isShowCover"
          @click="handleMakeCover"
          class="cover-btn"
          :class="isCover ? 'active' : ''"
          
        >
          <span class="material-symbols-outlined" style="font-size: 18px; font-variation-settings: 'FILL' 1;">star</span>
          {{ $t('lbl_EMPBE_Uploader_CoverVideo') }}
        </button>
      </div>


      <!-- Video Preview -->
      <div ref="previewRef" class="editor-canvas video-canvas">
        <!-- Background Layer -->
        <div class="video-background-layer">
          <img
            v-if="backgroundImage"
            :src="backgroundImage"
            class="background-video"
            alt="Background blur"
          />
          <div class="background-overlay"></div>
        </div>

        <!-- Main Video -->
        <video
          ref="videoRef"
          :src="activeFile.previewSrc"
          class="preview-video"
          controls
          loop
          muted
          crossOrigin="anonymous"
        ></video>
      </div>

      <!-- Thumbnail Section (Bottom) -->
      <div class="thumbnail-section">
        <div class="thumbnail-controls">
          <div>
            <span class="thumbnail-title">{{ $t('lbl_EMPBE_Uploader_EditVideoCover') }}</span>
            <p class="thumbnail-hint">{{ $t('lbl_EMPBE_Uploader_EditVideoCover_Hint') }}</p>
          </div>
          <div class="thumbnail-actions">
            <button @click="captureThumbnail" class="btn btn-outline-indigo " type="button">
              <span class="material-symbols-outlined" style="font-size: 18px;">wallpaper</span>
              {{ $t('lbl_EMPBE_Uploader_CaptureVideoCover') }}
            </button>
            <span class="text-or">{{ $t('lbl_EMPBE_OR') }}</span>
            <label class="btn btn-outline-indigo">
              <span class="material-symbols-outlined" style="font-size: 18px;">upload</span>
              {{ $t('lbl_EMPBE_Uploader_UploadPhoto') }}
              <input type="file" accept="image/*" @change="uploadThumbnail" class="hidden-input" />
            </label>
          </div>
        </div>
        <div class="separator"></div>
        <div class="thumbnail-preview-wrapper">
          <span class="thumbnail-preview-title">{{ $t('lbl_EMPBE_Uploader_VideoCoverPreview') }}</span>
          <template v-if="activeFile.thumbnail && activeFile.thumbnail.previewSrc">
            <div class="thumbnail-cropper-container" v-show="!activeFile.isLoadingInfo">
              <custom-cropper
                :key="activeFile.id.toString() + '-' + activeFile.thumbnail.id.toString() + '-' + imgCropperVersion.toString()"
                :image-src="activeFile.thumbnail.previewSrc"
                :edit-state="activeFile.thumbnail.editState"
                :aspect-ratio="parsedAspectRatio"
                :top-padding="0"
                @update="handleThumbnailUpdate"
                @minZoomChange="handleMinZoomChange"
              />
            </div>
            <div class="LoadingContainer" v-show="activeFile.isLoadingInfo">
              <LoadingSpinner />
            </div>
          </template>
          <template v-else>
            <div class="thumbnail-empty">
              <span class="material-symbols-outlined" style="font-size: 48px;">image_not_supported</span>
              <p>No thumbnail set</p>
            </div>
          </template>
          <span class="thumbnail-preview-hint">{{ $t('lbl_EMPBE_Uploader_VideoCoverPreview_Hint') }}</span>
        </div>
      </div>

    </template>
  </div>
</template>

<script>
import CustomCropper from './imgCropper.vue';
import LoadingSpinner from '../../../loadingSpinner.vue';
import { getThumbnailBlob } from '@/_helpers/media-uploader-utils.js';

export default {
  name: 'VideoEditor',
  components: {
    CustomCropper,
    LoadingSpinner
  },
  props: {
    activeFile: {
      type: Object,
      default: null
    },
    aspectRatioStr: {
      type: String,
      default: "9:16"
    },
    isCover: {
      type: Boolean,
      default: false
    },
    emptyStateBackgroundColor: {
      type: String,
      default: 'white'
    },
    isShowCover: {
      type: Boolean,
      default: false
    },
    isShowVideoPreviewBackground: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentMinZoom: 0.5,
      backgroundImage: "",
      backgroundImages: {},
      imgCropperVersion: 0
    };
  },
  computed: {
    parsedAspectRatio() {
      const parts = this.aspectRatioStr.split(':').map(Number);
      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1]) && parts[1] !== 0) {
        return parts[0] / parts[1];
      }
      return 0.5625; // 9:16 default
    },
  },
  watch: {
    activeFile: {
      handler(newVal) {
        this.currentMinZoom = 0.5;
        this.backgroundImage = "";
        if (this.$refs.videoRef) {
          this.$refs.videoRef.pause();
        }
        this.imgCropperVersion += 1;
        this.updateBackground();
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    this.updateBackground();
  },
  methods: {
    handleThumbnailUpdate(updates) {
      const { isFirstInit, ...updatedState } = updates;
      updatedState.isDirty = !(isFirstInit || false);
      this.$emit('thumbnail-update', updatedState);
    },
    handleMinZoomChange(val) {
      this.currentMinZoom = val;
    },
    async captureThumbnail() {
      if (!this.$refs.videoRef) return;
      
      const videoElem = this.$refs.videoRef;
      videoElem.pause();
      this.$emit('capture-thumbnail', videoElem);
    },
    uploadThumbnail(e) {
      if (e.target.files && e.target.files[0]) {
        const file = e.target.files[0];
        this.$emit('upload-thumbnail', file);
      }
      e.target.value = null;
    },
    handleMakeCover() {
      this.$emit('make-cover');
    },
    updateBackground() {
      if (!this.isShowVideoPreviewBackground || !this.activeFile || !this.activeFile.previewSrc || !this.activeFile.id) return;

      const id = this.activeFile.id.toString();
      if (this.backgroundImages[id]) {
        this.backgroundImage = this.backgroundImages[id];
        return;
      };
      
      getThumbnailBlob(this.activeFile.previewSrc, 1, 3000).then((res) => {
        const blob = res.blob;
        const thumbnailUrl = URL.createObjectURL(blob);
        this.$nextTick(() => {
          this.backgroundImages[id] = thumbnailUrl;
          this.backgroundImage = thumbnailUrl;
        });
      }).catch((e) => {
        this.$nextTick(() => {
          this.backgroundImages[id] = "";
          this.backgroundImage = "";
        });
        console.error("Update Video Background failed: ", e);
      });
    },
    revokeBackgroundUrls() {
      for (const key in this.backgroundImages) {
        const url = this.backgroundImages[key];
        if (!url) continue;
        URL.revokeObjectURL(url);
      }
    }
  }
};
</script>

<style scoped>
.LoadingContainer {
  width: 100%;
  aspect-ratio: 16/9;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #fff;
}

.LoadingContainer svg {
  height: 3rem;
  width: 3rem;
}

p{
  padding: 0;
  margin: 0;
}
.separator {
  width: 1px;
  height: 115px;
  background-color: #e2e8f0;
  align-self: center;
  
}
.video-editor {
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  max-height: 100%;
  gap:8px;
}

.editor-header-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(to bottom, rgba(0,0,0,0.5), transparent);
  pointer-events: none;
}

.editor-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: white;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0,0,0,0.5);
}

.cover-btn {
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background-color 0.2s;
  border: none;
  background-color: rgba(0, 0, 0, 0.40);
  color: white;
  cursor: pointer;
}
.cover-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
}
.cover-btn.active {
  background-color: #2563eb;
}

/* Video Canvas */
.video-canvas {
  flex: 1;
  position: relative;
  background: black;
  display: flex; 
  align-items: center; 
  justify-content: center;
  overflow: hidden;
  border-radius:1rem;

}
.preview-video {
  width: 100%;
  max-height: 100%;
}

.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #64748b;
}
.empty-icon {
  color: #475569;
  margin-bottom: 0.5rem;
}

/* Thumbnail Section */
.thumbnail-section {
  background-color: white;
  border-top: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-start;
  padding: 1rem;
  gap: 1rem;
  flex-shrink: 0;
  border-radius:1rem;
  
}
.thumbnail-hint{
  color: rgba(0, 0, 0, 0.60);
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: "inter", sans-serif;
  font-size: 0.75rem;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; 
}
.thumbnail-controls {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-self: stretch;
  width: 60%;
}
.thumbnail-title { 
  color: var(--On-Surface-80, rgba(0, 0, 0, 0.80));
  text-align: center;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 1.1375rem */
  margin-bottom: 0.25rem;
}
.thumbnail-actions { 
  display: flex; 
  align-items: center; 
  gap: 0.75rem; 
}
.text-or { 
  font-size: 0.75rem; 
  color: #94a3b8; 
}
.thumbnail-preview{ 
  font-size: 0.75rem; 
  color: #64748b; 
}

.btn-outline-indigo {
  border: 1px solid #c7d2fe; 
  color: #4f46e5; 
  background: white;
  padding: 0.5rem 0.75rem; 
  border-radius: 0.375rem; 
  font-size: 0.75rem; 
  font-weight: 500;
  display: flex; 
  align-items: center; 
  gap: 0.25rem; 
  cursor: pointer;
  margin-bottom: 0;
}
.btn-outline-indigo:hover { 
  background-color: #eef2ff; 
  border-color: #a5b4fc;
  color: #4f46e5;
}


.thumbnail-preview-wrapper {
  flex: 1;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  gap: 2px
}
.thumbnail-preview-title{
  color: rgba(0, 0, 0, 0.80);
  font-weight: 700;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; 
  font-family: "inter", sans-serif;
}
.thumbnail-preview-hint{
  color: rgba(0, 0, 0, 0.40);
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: "inter", sans-serif;
  font-size: 12px;
  font-style: italic;
  font-weight: 400;
  line-height: 130%; 
}
.thumbnail-cropper-container {
  aspect-ratio: 16/9;
  width: 100%;
  border-radius: 0.5rem;
  overflow: hidden;
  position: relative;
}
.thumbnail-empty {
  height: 100%;
  aspect-ratio: 16/9;
  background: #f1f5f9;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 0.75rem;
}

.hidden-input {
  display: none;
}

.material-symbols-outlined {
  font-family: 'Material Symbols Outlined';
  font-weight: normal;
  font-style: normal;
  font-size: 24px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  -webkit-font-feature-settings: 'liga';
  font-feature-settings: 'liga';
  -webkit-font-smoothing: antialiased;
}

/* Background Video Effect */
.video-background-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 0;
  pointer-events: none;
}

.background-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: blur(20px);
  transform: scale(1.2); /* Scale to hide blur edges */
}

.background-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.2);
  z-index: 1;
}

.preview-video {
  position: relative;
  z-index: 2;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

</style>